import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons'
import { GoogleMap, Marker, StandaloneSearchBox, useJsApiLoader } from '@react-google-maps/api'
import React, { useCallback, useRef, useState } from 'react'
import { BottomNav, ProgressHeader, TopBar } from './CarWithDriverShared'
import { useCwdLang } from './useCwdLang'

const GOOGLE_MAPS_LIBRARIES = ['places', 'geometry'] as any[]
const DEFAULT_CENTER = { lat: 13.7563, lng: 100.5018 }

interface FormData {
  branchName: string
  lat: number | null
  lng: number | null
  address: string
}

interface CarWithDriverStep1Props {
  formData: FormData
  onFormDataChange: (data: Partial<FormData>) => void
  currentStep: number
  totalSteps: number
  maxStep?: number
  stepProgress: Record<number, number>
  onExit: () => void
  onBack: () => void
  onNext: (data: any) => Promise<void>
}

const CarWithDriverStep1: React.FC<CarWithDriverStep1Props> = ({
  formData,
  onFormDataChange,
  currentStep,
  totalSteps,
  maxStep,
  onExit,
  onBack,
  onNext,
}) => {
  const { t } = useCwdLang()
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  })

  const [mapCenter, setMapCenter] = useState(
    formData.lat && formData.lng ? { lat: formData.lat, lng: formData.lng } : DEFAULT_CENTER,
  )
  const [isGeocoding, setIsGeocoding] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const mapRef = useRef<google.maps.Map | null>(null)
  const searchBoxRef = useRef<google.maps.places.SearchBox | null>(null)
  const geocoderRef = useRef<google.maps.Geocoder | null>(null)

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map
    geocoderRef.current = new window.google.maps.Geocoder()
  }, [])

  const handleMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!e.latLng || !geocoderRef.current) return
      const lat = e.latLng.lat()
      const lng = e.latLng.lng()
      setMapCenter({ lat, lng })
      setIsGeocoding(true)
      geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
        setIsGeocoding(false)
        if (status === 'OK' && results?.[0]) {
          const address = results[0].formatted_address
          const namePart = address.split(',')[0].trim()
          onFormDataChange({ lat, lng, address, branchName: namePart })
        } else {
          onFormDataChange({ lat, lng, address: `${lat.toFixed(6)}, ${lng.toFixed(6)}` })
        }
        setErrors({})
      })
    },
    [onFormDataChange],
  )

  const onPlacesChanged = useCallback(() => {
    const places = searchBoxRef.current?.getPlaces()
    if (!places || places.length === 0) return
    const place = places[0]
    const lat = place.geometry?.location?.lat()
    const lng = place.geometry?.location?.lng()
    if (lat === undefined || lng === undefined) return
    const newCenter = { lat, lng }
    setMapCenter(newCenter)
    mapRef.current?.panTo(newCenter)
    const address = place.formatted_address || ''
    const name = place.name || address.split(',')[0].trim()
    onFormDataChange({ lat, lng, address, branchName: name })
    setErrors({})
  }, [onFormDataChange])

  const handleSaveName = () => {
    if (editedName.trim()) onFormDataChange({ branchName: editedName.trim() })
    setIsEditingName(false)
  }

  const validate = () => {
    const errs: Record<string, string> = {}
    if (!formData.lat || !formData.lng) errs.location = t.step1.errLocation
    if (!formData.branchName.trim()) errs.name = t.step1.errName
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleNext = async () => {
    if (!validate()) return
    setIsSubmitting(true)
    await onNext({
      branchName: formData.branchName,
      lat: formData.lat,
      lng: formData.lng,
      address: formData.address,
    })
    setIsSubmitting(false)
  }

  const stepProgress = formData.lat ? 100 : 0

  return (
    <div style={pageStyle}>
      <TopBar />
      <ProgressHeader
        currentStep={currentStep}
        totalSteps={totalSteps}
        maxStep={maxStep}
        title={t.step1.title}
        currentStepProgress={stepProgress}
      />

      {/* Search box — outside the map */}
      <div style={{ padding: '0 16px 12px' }}>
        {isLoaded && window.google?.maps ? (
          <StandaloneSearchBox
            onLoad={(ref) => {
              searchBoxRef.current = ref
            }}
            onPlacesChanged={onPlacesChanged}
          >
            <div style={searchBoxContainerStyle}>
              <span style={{ fontSize: '16px', color: '#667085', flexShrink: 0 }}>🔍</span>
              <input type="text" placeholder={t.step1.searchPlaceholder} style={searchInputStyle} />
            </div>
          </StandaloneSearchBox>
        ) : (
          <div style={searchBoxContainerStyle}>
            <span style={{ fontSize: '16px', color: '#667085' }}>🔍</span>
            <input type="text" placeholder={t.common.loading} disabled style={searchInputStyle} />
          </div>
        )}
      </div>

      {/* Map — click to place marker */}
      <div style={{ margin: '0 16px', borderRadius: '8px', overflow: 'hidden' }}>
        {isLoaded && window.google?.maps ? (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '300px' }}
            center={mapCenter}
            zoom={14}
            onLoad={onMapLoad}
            onClick={handleMapClick}
            options={{
              fullscreenControl: false,
              zoomControl: true,
              streetViewControl: false,
              mapTypeControl: false,
              gestureHandling: 'greedy',
            }}
          >
            {formData.lat && formData.lng && (
              <Marker position={{ lat: formData.lat, lng: formData.lng }} />
            )}
          </GoogleMap>
        ) : (
          <div style={mapPlaceholderStyle}>{t.step1.loadingMap}</div>
        )}
      </div>

      {/* Location info card */}
      <div style={{ padding: '12px 16px', flex: 1 }}>
        {errors.location && (
          <p style={{ color: '#ef4444', fontSize: '12px', margin: '0 0 8px' }}>{errors.location}</p>
        )}
        {formData.lat ? (
          <div style={locationCardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              {isEditingName ? (
                <>
                  <input
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                    placeholder={t.step1.placeName}
                    autoFocus
                    style={nameInputStyle}
                  />
                  <CheckOutlined
                    onClick={handleSaveName}
                    style={{ color: '#52c41a', cursor: 'pointer', fontSize: '16px', flexShrink: 0 }}
                  />
                  <CloseOutlined
                    onClick={() => setIsEditingName(false)}
                    style={{ color: '#ef4444', cursor: 'pointer', fontSize: '16px', flexShrink: 0 }}
                  />
                </>
              ) : (
                <>
                  <span style={{ flex: 1, fontSize: '15px', fontWeight: '600', color: '#212427' }}>
                    {isGeocoding ? t.common.loading : formData.branchName || t.step1.unknownPlace}
                  </span>
                  {!isGeocoding && (
                    <button
                      onClick={() => {
                        setEditedName(formData.branchName)
                        setIsEditingName(true)
                      }}
                      style={editBtnStyle}
                    >
                      <EditOutlined style={{ fontSize: '12px' }} />
                      {t.common.edit}
                    </button>
                  )}
                </>
              )}
            </div>
            <p style={{ fontSize: '13px', color: '#667085', margin: 0, lineHeight: '1.4' }}>
              {formData.address}
            </p>
            {errors.name && (
              <p style={{ color: '#ef4444', fontSize: '12px', margin: '6px 0 0' }}>{errors.name}</p>
            )}
          </div>
        ) : (
          <p
            style={{
              fontSize: '13px',
              color: '#667085',
              margin: 0,
              textAlign: 'center',
              paddingTop: '8px',
            }}
          >
            {t.step1.tapMapHint}
          </p>
        )}
      </div>

      <BottomNav
        onBack={onBack}
        onNext={handleNext}
        nextDisabled={!formData.lat || !formData.lng}
        loading={isSubmitting}
      />
    </div>
  )
}

const pageStyle: React.CSSProperties = {
  fontFamily: 'Poppins, sans-serif',
  backgroundColor: '#fff',
  minHeight: '100vh',
  maxWidth: '500px',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
}

const searchBoxContainerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  border: '1px solid #D6DAE0',
  borderRadius: '8px',
  padding: '0 12px',
  backgroundColor: '#fff',
}

const searchInputStyle: React.CSSProperties = {
  border: 'none',
  outline: 'none',
  width: '100%',
  padding: '12px 0',
  fontSize: '14px',
  fontFamily: 'Poppins, sans-serif',
  backgroundColor: 'transparent',
}

const mapPlaceholderStyle: React.CSSProperties = {
  width: '100%',
  height: '300px',
  backgroundColor: '#f0f0f0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#667085',
  fontSize: '14px',
}

const locationCardStyle: React.CSSProperties = {
  padding: '12px',
  backgroundColor: '#f5f5f5',
  borderRadius: '8px',
}

const nameInputStyle: React.CSSProperties = {
  flex: 1,
  border: '1px solid #0048B3',
  borderRadius: '4px',
  padding: '4px 8px',
  fontSize: '15px',
  fontWeight: '500',
  fontFamily: 'Poppins, sans-serif',
  outline: 'none',
}

const editBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 8px',
  backgroundColor: '#EFF6FF',
  border: '1px solid #91d5ff',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '12px',
  color: '#0048B3',
  fontFamily: 'Poppins, sans-serif',
  flexShrink: 0,
}

export default CarWithDriverStep1
